import isAfter from "date-fns/isAfter";
import isBefore from "date-fns/isBefore";
import isEqual from "date-fns/isEqual";
import { BaseFormBlock, FieldRecord, FormField } from "../FormTypes";
import {
  isBooleanField,
  isDateField,
  isMultiStringField,
  isNumericField,
  isStringField,
} from "./typePredicates";

export const hasConditionalLogic = (block: BaseFormBlock): boolean => {
  if (!block.conditions) {
    return false;
  }

  const {
    conditions: { is, not, gte, lte },
  } = block;

  return [is, not, gte, lte].some((c) => !!c) ? true : false;
};

const tryMatchBoolean = (
  responseValue: boolean | null | undefined,
  shouldMatch: boolean | string
) => {
  let matchValue = false;
  if (typeof shouldMatch === "boolean") {
    matchValue = shouldMatch;
  } else {
    try {
      matchValue = JSON.parse(shouldMatch);
    } catch (e) {
      console.error("Invalid match value received for Boolean response; ", e);
    }
  }
  return responseValue === matchValue;
};

export const shouldDisplayField = (
  fields: FieldRecord,
  field: FormField
): boolean => {
  // if field is prefilled, hide the field and return early
  if (field.isPrefilled) {
    return false;
  }

  // If conditions is undefined, show the field and return early
  if (!field.conditions) {
    return true;
  }

  // If one or more of the rule keys has been defined, field hides by default,
  // otherwise it's displayed.
  let shouldDisplay = !hasConditionalLogic(field);

  const {
    conditions: { is, not, gte, lte },
  } = field;

  if (is) {
    Object.entries(is).forEach(([key, matches]) => {
      const matchField = fields[key];

      if (
        isStringField(matchField) &&
        typeof matchField.currentValue === "string" &&
        matchField.currentValue !== ""
      ) {
        shouldDisplay = matches.some(
          (m) =>
            matchField.currentValue &&
            m.trim().toLowerCase() ===
              matchField.currentValue.trim().toLowerCase()
        );
      } else if (isBooleanField(matchField)) {
        shouldDisplay =
          matches.some((m) => tryMatchBoolean(matchField.currentValue, m)) ||
          shouldDisplay;
      } else if (isMultiStringField(matchField)) {
        shouldDisplay =
          matches.some(
            (m) =>
              matchField.currentValue &&
              (matchField.currentValue as Set<string>).has(m)
          ) || shouldDisplay;
      }
    });
  }

  if (not) {
    Object.entries(not).forEach(([key, matches]) => {
      const matchField = fields[key];

      if (
        isStringField(matchField) &&
        typeof matchField.currentValue === "string" &&
        matchField.currentValue !== ""
      ) {
        shouldDisplay =
          !matches.some((m) => {
            return (
              matchField.currentValue &&
              m.trim().toLowerCase() ===
                matchField.currentValue.trim().toLowerCase()
            );
          }) || shouldDisplay;
      } else if (isBooleanField(matchField)) {
        shouldDisplay =
          matches.some((m) => !tryMatchBoolean(matchField.currentValue, m)) ||
          shouldDisplay;
      } else if (isMultiStringField(matchField)) {
        shouldDisplay =
          matches.some(
            (m) =>
              !(
                matchField.currentValue &&
                (matchField.currentValue as Set<string>).has(m)
              )
          ) || shouldDisplay;
      }
    });
  }

  if (gte) {
    Object.entries(gte).forEach(([key, match]) => {
      const matchField = fields[key];
      const response =
        matchField.currentValue !== null
          ? Number(matchField.currentValue)
          : null;

      if (isNumericField(matchField) && response && !isNaN(response)) {
        shouldDisplay = response >= Number(match) || shouldDisplay;
      } else if (isDateField(matchField)) {
        const response = new Date(fields[key].currentValue as string);
        const matchValue = new Date(match);

        shouldDisplay =
          isAfter(matchValue, response) ||
          isEqual(matchValue, response) ||
          shouldDisplay;
      }
    });
  }

  if (lte) {
    Object.entries(lte).forEach(([key, match]) => {
      const matchField = fields[key];
      const response =
        matchField.currentValue !== null
          ? Number(matchField.currentValue)
          : null;

      if (isNumericField(matchField) && response && !isNaN(response)) {
        shouldDisplay = response <= Number(match) || shouldDisplay;
      } else if (isDateField(matchField)) {
        const response = new Date(fields[key].currentValue as string);
        const matchValue = new Date(match);

        shouldDisplay =
          isBefore(matchValue, response) ||
          isEqual(matchValue, response) ||
          shouldDisplay;
      }
    });
  }

  return shouldDisplay;
};
